import { useSEO, BASE } from "@/lib/seo";
import ArticleShell, { H2, H3, P, SeeAlso } from "@/components/site/ArticleShell";

const COMPARISON = [
  ["Rapport au modèle", "Fidélité au sujet, canon de beauté idéal", "Liberté totale : figuration, déformation ou abstraction"],
  ["Matériaux", "Marbre, bronze, plâtre nobles", "Tous les matériaux, du bronze à la résine, en passant par l'objet récupéré"],
  ["Commande", "État, Église, portraits officiels", "Commande publique, collectionneurs privés, art in situ"],
  ["Savoir-faire", "Pratiques d'atelier codifiées, hiérarchie des genres", "Techniques mixtes, hybridation avec le design et le numérique"],
  ["Diffusion", "Salons académiques", "Galeries, foires, ateliers ouverts, vente en direct"],
];

export default function DefinitionPage() {
  useSEO(
    "Qu'est-ce qu'un Sculpteur Contemporain Français ? — Définition & Rôle",
    "Définition approfondie du sculpteur contemporain en France, son rôle dans la création artistique moderne, la transmission des savoir-faire et la valeur de l'œuvre originale.",
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Qu'est-ce qu'un sculpteur contemporain français ?",
      description:
        "Définition, histoire et rôle du sculpteur contemporain en France : ce qui le distingue de la sculpture classique, sa formation et son ancrage.",
      inLanguage: "fr-FR",
      mainEntityOfPage: `${BASE}/definition-sculpteur-contemporain`,
      author: { "@type": "Organization", name: "Sculpteur Français", url: `${BASE}` },
      publisher: { "@type": "Organization", name: "Sculpteur Français", url: `${BASE}` },
    },
    { type: "article" }
  );

  return (
    <ArticleShell
      testId="definition-page"
      eyebrow="Définition — le métier"
      titleLines={["Qu'est-ce qu'un", "sculpteur contemporain", "français ?"]}
      lead="Ni statuaire d'apparat ni pur concepteur : le sculpteur contemporain est un artiste qui pense le volume. Définition, repères historiques et distinctions essentielles."
      image={{
        src: "/images/victoire-samothrace-louvre.webp",
        alt: "Victoire de Samothrace, célèbre sculpture grecque antique en marbre, musée du Louvre",
        caption: "Victoire de Samothrace, v. 190 av. J.-C., marbre, musée du Louvre.",
        credit: "Photo : Shonagon, CC0, via Wikimedia Commons",
        creditUrl:
          "https://commons.wikimedia.org/wiki/File:Victoire_de_Samothrace_-_Mus%C3%A9e_du_Louvre_-_vue_large.jpg",
        to: "/lexique-sculpture/ronde-bosse",
      }}
    >
      <H2>Une définition vivante</H2>
      <H2>Qu'est-ce que la sculpture contemporaine ?</H2>
      <P>
        La sculpture contemporaine est l'art du volume créé par les artistes
        d'aujourd'hui — modelage, taille, assemblage, moulage — dans tous les
        matériaux, du bronze traditionnel à la résine, du bois au métal. Elle
        se distingue de la sculpture classique par sa liberté de sujet, de
        forme et d'échelle : elle n'a plus vocation à représenter ou célébrer,
        mais à questionner l'espace, la matière et celui qui la regarde. Elle
        se vit autant dans les galeries et les collections privées que dans
        l'espace public.
      </P>

      <P>
        Un sculpteur contemporain est un artiste qui crée des œuvres en trois
        dimensions — modelées, taillées, assemblées ou fondues — en dialogue
        avec les questions de son époque. En France, il hérite d'une tradition
        pluriséculaire tout en s'en affranchissant : la figure humaine n'est
        plus un impératif, le marbre n'est plus un sommet, et l'atelier n'est
        plus un lieu clos.
      </P>
      <P>
        « Contemporain » ne désigne pas un style mais une période : celle des
        artistes vivants ou récemment disparus, dont le travail s'inscrit dans
        les débats actuels — la place du corps, la matière, l'espace public,
        l'objet industriel. Deux sculpteurs contemporains peuvent donc ne
        partager ni esthétique ni technique, tout en appartenant au même moment
        de l'art.
      </P>

      <H2>Ce qui distingue le contemporain du classique</H2>
      <P>
        La rupture s'est opérée progressivement, de Rodin — qui libère la
        sculpture du socle académique — aux avant-gardes du XXᵉ siècle.
        Aujourd'hui, la différence tient moins à un style qu'à une liberté
        revendiquée : de sujet, de matériau, d'échelle et de destination.
      </P>
      <div className="mt-8 overflow-x-auto border border-ink/15" data-testid="comparison-table">
        <table className="w-full min-w-[640px] border-collapse text-left">
          <thead>
            <tr className="border-b border-ink/15 bg-cream">
              <th className="p-4 font-mono text-[11px] uppercase tracking-[0.18em] text-bronze">Repère</th>
              <th className="p-4 font-mono text-[11px] uppercase tracking-[0.18em] text-smoke">Sculpture classique</th>
              <th className="p-4 font-mono text-[11px] uppercase tracking-[0.18em] text-smoke">Sculpture contemporaine</th>
            </tr>
          </thead>
          <tbody>
            {COMPARISON.map(([label, classic, modern]) => (
              <tr key={label} className="border-b border-ink/10 last:border-0">
                <td className="p-4 font-serif text-lg text-ink">{label}</td>
                <td className="p-4 text-sm font-light leading-relaxed text-smoke">{classic}</td>
                <td className="p-4 text-sm font-light leading-relaxed text-smoke">{modern}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <H2>Comment se forme un sculpteur aujourd'hui</H2>
      <P>
        Il n'existe plus de voie unique. Beaucoup passent par les écoles
        supérieures d'art (les Beaux-Arts et leurs équivalents), où l'on
        apprend autant l'histoire de l'art que le modelage ou la fonte.
        D'autres se forment dans les ateliers de compagnonnage, auprès de
        fondeurs, de tailleurs de pierre ou de céramistes. Une part non
        négligeable de sculpteurs en activité sont autodidactes : la légitimité
        vient alors de l'œuvre elle-même, de sa cohérence et de sa présence sur
        la scène artistique.
      </P>

      <H2>La France, terre de sculpture</H2>
      <P>
        De la statuaire gothique à Rodin, de Maillol à César, la sculpture
        occupe une place centrale dans l'histoire de l'art français. Cette
        filiation pèse autant qu'elle nourrit : le sculpteur français
        contemporain travaille dans un pays où la commande publique reste
        vivante — la règle du « 1 % artistique » impose la présence d'œuvres
        dans les bâtiments publics — et où les fonderies d'art perpétuent des
        savoir-faire uniques, comme la fonte à la cire perdue.
      </P>

      <H2>Un métier entre artisanat et art</H2>
      <P>
        Être sculpteur aujourd'hui, c'est tenir ensemble deux exigences : la
        maîtrise manuelle — modeler, mouler, patiner, assembler — et une
        démarche artistique singulière, un propos. Certains réalisent tout
        eux-mêmes, de l'esquisse à la patine ; d'autres confient la fonte ou la
        mise au point à des artisans spécialisés, sans que l'œuvre perde rien
        de sa légitimité, tant que la main de l'artiste gouverne le processus.
      </P>

      <blockquote className="mt-14 border-l-2 border-bronze py-2 pl-8" data-testid="definition-quote">
        <p className="font-serif text-2xl italic leading-snug text-ink">
          « La sculpture contemporaine n'imite plus le monde : elle le
          questionne en volume. »
        </p>
        <cite className="mt-4 block font-mono text-[11px] uppercase not-italic tracking-[0.18em] text-smoke">
          Réflexion d'atelier — propos recueillis auprès de sculpteurs en activité
        </cite>
      </blockquote>

      <SeeAlso
        links={[
          { to: "/courants-et-mouvements", label: "Les courants de la sculpture contemporaine" },
          { to: "/materiaux-et-techniques", label: "Matériaux et techniques" },
          { to: "/lexique-sculpture", label: "Le lexique des termes" },
        ]}
      />
    </ArticleShell>
  );
}
